import React, { useState, useEffect, useRef } from "react";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import { WebSocketMessage } from "../../types/websocket";
import "./DiceRollDisplay.css";

interface DiceRollDisplayProps {
  sessionId: string;
  userId?: string;
}

const getDataProp = (data: unknown, prop: string): unknown => {
  if (typeof data === "object" && data !== null && prop in data) {
    return (data as Record<string, unknown>)[prop];
  }
  return undefined;
};

export const DiceRollDisplay: React.FC<DiceRollDisplayProps> = ({ sessionId, userId }) => {
  const { getDiceRolls, getUserUsername } = useWebSocketContext();
  const [displayRoll, setDisplayRoll] = useState<WebSocketMessage | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const diceRolls = getDiceRolls(sessionId);
  const lastCountRef = useRef(diceRolls.length);
  const lastRollIdRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const latestRoll = diceRolls[diceRolls.length - 1];
    lastCountRef.current = diceRolls.length;
    lastRollIdRef.current = latestRoll ? latestRoll.id : null;
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentLength = diceRolls.length;
    if (currentLength > lastCountRef.current) {
      const latestRoll = diceRolls[currentLength - 1];
      const data = latestRoll.data;
      const isHistorical = getDataProp(data, "isHistorical") as boolean | undefined;

      // Only show if it's not historical, it's a new ID, and it has actual roll data
      if (
        !isHistorical &&
        latestRoll.id !== lastRollIdRef.current &&
        (latestRoll.type !== "dice_roll" || getDataProp(data, "total") !== undefined)
      ) {
        const isPrivate = getDataProp(data, "isPrivate") as boolean | undefined;
        if (isPrivate && userId && latestRoll.senderId !== userId) {
          lastCountRef.current = currentLength;
          lastRollIdRef.current = latestRoll.id;
          return;
        }

        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }

        setDisplayRoll(latestRoll);
        setIsVisible(true);
        setIsHiding(false);
        lastRollIdRef.current = latestRoll.id;

        timerRef.current = setTimeout(() => {
          setIsHiding(true);
          timerRef.current = setTimeout(() => {
            setIsVisible(false);
            setIsHiding(false);
            setDisplayRoll(null);
          }, 500);
        }, 5000);
      }
    }
    lastCountRef.current = currentLength;
  }, [diceRolls.length, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleDismiss = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsHiding(true);
    timerRef.current = setTimeout(() => {
      setIsVisible(false);
      setIsHiding(false);
      setDisplayRoll(null);
    }, 500);
  };

  if (!displayRoll || !isVisible) return null;

  const data = displayRoll.data;
  const formula = (getDataProp(data, "formula") as string) || "";
  const results = (getDataProp(data, "results") as number[]) || [];
  const total = (getDataProp(data, "total") as number) || 0;
  const rollName = getDataProp(data, "rollName") as string | undefined;
  const characterName = getDataProp(data, "characterName") as string | undefined;
  const isCritical = (getDataProp(data, "isCritical") as boolean) || false;
  const isPrivate = (getDataProp(data, "isPrivate") as boolean) || false;

  const isFumble =
    Array.isArray(results) &&
    results.length === 1 &&
    results[0] === 1 &&
    formula.toLowerCase().includes("d20");

  const rollerName =
    characterName || getUserUsername(displayRoll.senderId) || `User ${displayRoll.senderId}`;

  const borderColor = isCritical
    ? "border-green-500"
    : isFumble
      ? "border-red-500"
      : "border-indigo-500";
  const shadowStyle = isCritical
    ? "0 0 40px rgba(74,222,128,0.3)"
    : isFumble
      ? "0 0 40px rgba(239,68,68,0.3)"
      : "0 0 40px rgba(99,102,241,0.2)";
  const totalColor = isCritical ? "text-green-400" : isFumble ? "text-red-400" : "text-blue-300";
  const progressColor = isCritical ? "bg-green-500" : isFumble ? "bg-red-500" : "bg-indigo-500";

  return (
    <div
      className={`absolute inset-0 flex items-center justify-center z-20 cursor-pointer ${isHiding ? "dice-roll-overlay hiding" : "dice-roll-overlay"}`}
      style={{ background: "rgba(0,0,0,0.78)" }}
      onClick={handleDismiss}
      data-testid="dice-roll-display"
    >
      <div
        className={`relative bg-gray-900 border-2 ${borderColor} rounded-2xl p-8 text-center max-w-sm w-full mx-4 cursor-default ${isHiding ? "dice-roll-card hiding" : "dice-roll-card"}`}
        style={{ boxShadow: shadowStyle }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-4xl mb-3">🎲</div>

        {/* Roller info */}
        <div className="text-sm mb-1 flex items-center justify-center flex-wrap gap-1">
          <span className="text-white font-bold text-base">{rollerName}</span>
          <span className="text-gray-400">rolled</span>
          {rollName && <span className="text-yellow-400 font-semibold">{rollName}</span>}
          {isPrivate && <span className="text-purple-400 text-xs font-medium">[Private]</span>}
        </div>

        {/* Formula */}
        <div className="text-indigo-300 font-mono text-lg mb-5">{formula}</div>

        {/* Individual dice */}
        {Array.isArray(results) && results.length > 0 && (
          <div className="flex items-center justify-center flex-wrap gap-2 mb-5">
            {(results as number[]).map((r, i) => (
              <div
                key={i}
                className={`die-box w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold ${
                  r === 20 && formula.toLowerCase().includes("d20")
                    ? "border-green-400 bg-green-900/40 text-green-300"
                    : r === 1 && formula.toLowerCase().includes("d20")
                      ? "border-red-400 bg-red-900/40 text-red-300"
                      : "border-gray-600 bg-gray-800 text-white"
                }`}
              >
                {r}
              </div>
            ))}
          </div>
        )}

        {/* Total */}
        <div className={`text-7xl font-black leading-none mb-3 ${totalColor}`}>{total}</div>

        {isCritical && (
          <div className="critical-label text-green-400 font-bold text-lg mb-2">
            ✨ CRITICAL HIT!
          </div>
        )}
        {isFumble && (
          <div className="text-red-400 font-bold text-lg mb-2">💀 CRITICAL FAILURE!</div>
        )}

        {/* Progress bar */}
        <div className="mt-4 h-1 bg-gray-700 rounded-full overflow-hidden">
          <div className={`h-full rounded-full dice-progress-bar ${progressColor}`} />
        </div>

        <div className="text-gray-600 text-xs mt-2">Click anywhere to dismiss</div>
      </div>
    </div>
  );
};
